/* eslint-disable @typescript-eslint/no-namespace */
import { Moment } from 'moment';
import { formatNumber } from '@angular/common';

import { Maturity, Series, DealVersion } from '../../types/deal.types';
import { DataExport } from './data-export';
import { OptionsType } from '../../helpers/issue/maturity';
import { getSecurityTypeLabel } from '../../helpers/issue/security-type';
import { getCallFeatureForSecurityType } from '../../helpers/issue/call-feature';
import { DealSeries } from '../../helpers/issue/series';
import {
  uniqueRatingModels,
  uniqueFullRatingModels,
  formatFullRatingsForExport
} from '../../helpers/issue/ratings';
import {
  isSeriesNotTaxable,
  taxStatusGetterForSeries,
  hasNotTaxableSeries
} from '../../grid/utils/value-getters/tax-status';
import { blackoutValueGetter } from '../../grid/utils/value-getters/maturity-getter';
import { USTreasuries } from '../../helpers/treasuries';
import { DealSummaryGridColumnName } from '../../grid/types/column.types';
import { GridMaturityModelWithAnalytics } from '../../types/deal.viewmodel.types';
import { BenchmarkValue } from '../../grid/utils/value-getters/benchmark-spread-getter';

export namespace MaturityDataExport {
  import ExportDataValueProvider = DataExport.ExportDataValueProvider;
  import ExportFormatProvider = DataExport.ExportFormatProvider;
  import ExportColumnDefinition = DataExport.ExportColumnDefinition;

  export class MaturityExportColumn extends ExportColumnDefinition {
    constructor(
      public headerName: string,
      public valueProvider: ExportMaturityValueProvider,
      options?: {
        formatProvider?: ExportFormatProvider;
        conditionToInclude?: (data: DealVersion) => boolean;
        mappers?: ((value: any) => any)[];
      }
    ) {
      super(options as unknown);
    }
  }

  export interface ExportMaturityValueProvider extends ExportDataValueProvider {
    getValue(maturity: Maturity): any;
  }

  export interface MaturityValueGetterContext {
    noteExclusions: string[];
    showBlackoutText: boolean;
    getLocale: () => string;
    getSeries: () => Series;
  }

  export abstract class DefaultMaturityValueProvider
    implements ExportMaturityValueProvider {
    public context: MaturityValueGetterContext;
    getValue(maturity: Maturity): void {
      return;
    }
  }

  export class DefaultFieldMaturityValueProvider extends DefaultMaturityValueProvider {
    constructor(public field: string) {
      super();
    }
    getValue(maturity: Maturity): any {
      return maturity[this.field];
    }
  }

  export class NumberBondsValueGetterMaturityValueProvider extends DefaultFieldMaturityValueProvider {
    getValue(maturity: Maturity): any {
      return super.getValue(maturity);
    }
  }

  export class PrincipalAmountMaturityValueProvider extends DefaultFieldMaturityValueProvider {
    getValue(maturity: Maturity): any {
      const result = super.getValue(maturity);
      return result === 0 ? '' : result;
    }
  }

  export class BaseValueGetterMaturityValueProvider extends DefaultMaturityValueProvider {
    constructor(public valueGetter: (arg) => unknown) {
      super();
    }
    getValue(maturity): any {
      return this.valueGetter({ data: maturity, context: this.context });
    }
  }

  export class BenchmarkCurvesValueGetterMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: GridMaturityModelWithAnalytics): BenchmarkValue {
      if (
        hasNotTaxableSeries([
          taxStatusGetterForSeries(this.context.getSeries())
        ])
      ) {
        return maturity[DealSummaryGridColumnName.BenchmarkSpread];
      }
      return null;
    }
  }

  export class StringValueGetterMaturityValueProvider extends BaseValueGetterMaturityValueProvider {
    getValue(maturity): any {
      let value = super.getValue(maturity);
      value =
        Array.isArray(value) &&
        value.length === 1 &&
        value[0].indexOf(OptionsType.Blank) !== -1
          ? ''
          : value;
      return value;
    }
  }

  export class NoteValueGetterMaturityValueProvider extends BaseValueGetterMaturityValueProvider {
    getValue(maturity: Maturity): any {
      const value = super.getValue(maturity);
      return Object.keys(value)
        .map((key): string =>
          Array.isArray(value[key])
            ? value[key].join(', ')
            : key === 'freeText'
            ? `${value[key].header || ''}${value[key].text || ''}`
            : value[key]
        )
        .filter((note) => !!note)
        .join(', ');
    }
  }

  export class TaxEqYldValueGetterMaturityValueProvider extends BaseValueGetterMaturityValueProvider {
    getValue(maturity): any {
      const taxStatus = taxStatusGetterForSeries(
        this.context.getSeries(),
        true
      );
      let value =
        taxStatus === 'T-E' || taxStatus === 'CorpCUSIP'
          ? super.getValue(maturity)
          : null;
      //catering for 0% MTR returning -
      if (value !== null) {
        const val = Number(
          formatNumber(value, this.context.getLocale(), '1.3')
        );
        value = !Number.isNaN(val) ? val : value;
      }
      return value;
    }
  }
  export class TaxYqSpreadMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: GridMaturityModelWithAnalytics): any {
      if (
        !isSeriesNotTaxable(this.context.getSeries()) ||
        !maturity ||
        !maturity[DealSummaryGridColumnName.TaxEqSpread]
      ) {
        return null;
      }

      const taxEqSpread = maturity[DealSummaryGridColumnName.TaxEqSpread].tes;

      return `${taxEqSpread > 0 ? '+' : ''}${formatNumber(
        taxEqSpread,
        this.context.getLocale(),
        '1.0-0'
      )}`;
    }
  }

  export class TesBenchmarkYearMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: GridMaturityModelWithAnalytics) {
      if (
        !isSeriesNotTaxable(this.context.getSeries()) ||
        !maturity ||
        !maturity[DealSummaryGridColumnName.TaxEqSpread]
      ) {
        return null;
      }

      const tesValue = maturity[DealSummaryGridColumnName.TaxEqSpread];
      return USTreasuries.treasuryLabelMap[tesValue.treasuryFeed];
    }
  }

  export class ImpliedYieldMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity): any {
      let value;
      if (maturity) {
        value = blackoutValueGetter(DealSummaryGridColumnName.ImpliedYield)({
          data: maturity
        } as any);
        if (value) {
          value = `${formatNumber(
            value.pricingValue,
            this.context.getLocale(),
            '1.3'
          )} (${value.treasuryYear})`;
        }
      }
      return value ? value : '';
    }
  }

  export class ImpliedSpreadMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: GridMaturityModelWithAnalytics): number {
      if (
        isSeriesNotTaxable(this.context.getSeries()) ||
        !maturity ||
        !USTreasuries.impliedSpreadForMaturityVisible(maturity) ||
        !maturity[DealSummaryGridColumnName.ImpliedSpread]
      ) {
        return null;
      }
      return Math.round(
        maturity[DealSummaryGridColumnName.ImpliedSpread].pricingValue
      );
    }
  }

  export class ImpliedSpreadBenchmarkYearMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: GridMaturityModelWithAnalytics) {
      if (
        isSeriesNotTaxable(this.context.getSeries()) ||
        !maturity ||
        !USTreasuries.impliedSpreadForMaturityVisible(maturity) ||
        !maturity[DealSummaryGridColumnName.ImpliedSpread]
      ) {
        return null;
      }
      const impliedSpreadValue =
        maturity[DealSummaryGridColumnName.ImpliedSpread];
      return USTreasuries.getTreasuryLabel(impliedSpreadValue.treasuryYear);
    }
  }

  export class SeriesMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: Maturity): any {
      return `Series ${this.context.getSeries().code}`;
    }
  }

  export class EnhancementMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: Maturity): string {
      const seriesLevelEnhancement = this.context.getSeries().insurer;
      const maturityLevelEnhancement = maturity.insurer;

      return maturityLevelEnhancement || seriesLevelEnhancement || '-';
    }
  }

  export class FirstCallDateMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: Maturity): any {
      const callFeature =
        maturity.callFeature ||
        this.context
          .getSeries()
          .callFeatures.find((cf) => cf.securityType === maturity.securityType);
      let callDate = callFeature ? callFeature.firstCallDate : undefined;
      callDate =
        maturity.chartDate && callDate && callDate.isBefore(maturity.chartDate)
          ? callDate
          : undefined;
      return callDate;
    }
  }

  export class FirstCouponDateMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: Maturity): Moment[] {
      return DealSeries.getFirstCouponDateForSeries([this.context.getSeries()]);
    }
  }

  export class DatedDateMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: Maturity): Moment[] {
      return DealSeries.getDatedDateForSeries([this.context.getSeries()]);
    }
  }

  export class SecurityTypeMaturityValueProvider extends DefaultFieldMaturityValueProvider {
    constructor() {
      super('securityType');
    }
    getValue(maturity: Maturity): any {
      return getSecurityTypeLabel(super.getValue(maturity));
    }
  }

  export class CallFeatureMaturityValueProvider extends DefaultFieldMaturityValueProvider {
    constructor() {
      super('callFeature');
    }
    getValue(maturity: Maturity): any {
      let value = super.getValue(maturity);
      value =
        (value && value.displayText) ||
        getCallFeatureForSecurityType(
          this.context.getSeries(),
          maturity.securityType
        );
      return value;
    }
  }

  export class EsgMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: Maturity): any {
      const esg = this.context.getSeries().esgCertification;
      let result;
      if (esg) {
        result = `${esg.esgCertType}, ${esg.esgCertVerifier}${
          esg.esgPreapprovedInd ? ', Pre-Qualified' : ''
        }`;
      }
      return result;
    }
  }

  export class BQMaturityValueProvider extends DefaultMaturityValueProvider {
    getValue(maturity: Maturity): any {
      const bq = this.context.getSeries().isBankQualified;
      if (bq === null) {
        return null;
      }
      return bq ? 'Yes' : 'No';
    }
  }

  export class MaturitySeriesFieldValueProvider extends DefaultMaturityValueProvider {
    constructor(public fieldName: string) {
      super();
    }
    getValue(maturity: Maturity): any {
      return this.context.getSeries()[this.fieldName];
    }
  }

  export class MaturitySeriesTaxStatusValueProvider extends DefaultMaturityValueProvider {
    constructor(public abbreviate: boolean) {
      super();
    }

    getValue(_maturity: Maturity): string {
      return taxStatusGetterForSeries(
        this.context.getSeries(),
        this.abbreviate
      );
    }
  }

  export class RatingsMaturityValueProvider extends DefaultMaturityValueProvider {
    constructor(public ratingName: string) {
      super();
    }
    getValue(maturity: Maturity): string[] {
      const [ratings, underlyingRatings] = uniqueRatingModels(
        [this.context.getSeries()],
        this.ratingName
      );

      const resultRatings = ratings.map((r) => r.rating || r.shortTerm || '-');

      const resultUnderlyingRatings = underlyingRatings.map(
        (r) => r.rating || r.shortTerm || '-'
      );
      const seriesLevelRating = [...resultRatings, ...resultUnderlyingRatings];

      const maturityLevelRating =
        !!maturity.ratings.length &&
        maturity.ratings
          .filter((rating) => rating.agency === this.ratingName)
          .map((r) => r.rating || r.shortTerm);

      return maturityLevelRating || seriesLevelRating;
    }
  }

  export class SeriesRatingsValueProvider extends DefaultMaturityValueProvider {
    constructor(public agencyName: string) {
      super();
    }
    getValue(maturity: Maturity): string[] {
      const seriesFullRatings = uniqueFullRatingModels(
        [this.context.getSeries()],
        this.agencyName
      );

      return formatFullRatingsForExport(seriesFullRatings);
    }
  }
}
